// Pure projection of the committed AgentTrace into the drawer's replay rows.

import type { AgentTrace, AgentTraceSchema } from '@/shared/contracts/analytics/v1';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { formatDayMonth, formatElapsedMinutes } from '@/versions/v1/shared/clock.derive';
import { assertUsd, formatCurrency } from '@/versions/v1/shared/money';
import { envelopeClock } from '@/versions/v1/modules/advisory/AnalysisRun.transform';
import { PLANT_ROLES } from '@/versions/v1/shared/bunker-hill-seed';

export type AgentRecommendation = AgentTraceSchema.AgentRecommendation;
export type AgentTool = NonNullable<AgentTraceSchema.Tool>;
export type AgentDecisionKind = 'approved' | 'modified' | 'rejected';

export type AgentInvestigationRow = {
  tool: AgentTool;
  arguments: Record<string, string>;
  argumentLabel: string;
  reasoning: string;
  resultExcerpt: string;
  elapsedMs: number;
};

export type AgentApprovalRecord = {
  caseId: string;
  causeId: string;
  kind: AgentDecisionKind;
  reason: string;
  approver: string;
  at: string;
};

function argumentLabel(tool: AgentTool, args: Record<string, string>): string {
  if (tool === 'get_trend') {
    const [start, end] = (args.window ?? '').split('/');
    return `${args.tag ?? ''} · ${clockRange(start ?? '', end ?? '')}`;
  }
  if (tool === 'get_control_plan_row') return 'Rougher feed pH';
  if (tool === 'get_tarp_ladder') return 'Rougher feed pH response';
  if (tool === 'get_shift_log') {
    const [start, end] = (args.window ?? '').split('/');
    return clockRange(start ?? '', end ?? '');
  }
  return '';
}

const SEED_ID_LABELS: readonly (readonly [string, string])[] = [
  ['cp-rougher-ph-01', 'Rougher feed pH'],
  ['tarp-rougher-ph-01', 'Rougher feed pH response'],
  ['fmea-zn-lime-underfeed', 'lime supply shortfall'],
  ['fm-zn-ph', 'pH drift / under-lime'],
  ['rl-zn-ph-limit', 'pH limit'],
  ['rl-zn-ph-trend', 'pH falling trend'],
];

/** Keep fixture keys available to the transform, while showing labels on the glass. */
function publicAgentText(value: string): string {
  let label = value;
  for (const [id, replacement] of SEED_ID_LABELS) label = label.split(id).join(replacement);
  return label
    .replace(/\b(\d+)m(?=\s+aggregate\b)/g, (_match, minutes: string) => formatElapsedMinutes(Number(minutes)))
    .replace(/\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?\b/g,
      (stamp) => caseClockStamp(stamp))
    .replace(/\b(?:fm|cp|tarp|rl|fmea)-[a-z0-9][a-z0-9-]*\b/gi, 'linked row');
}

function caseClockStamp(stamp: string): string {
  const at = envelopeClock(stamp) as SeriesTime;
  return `${formatDayMonth(at)} ${at.slice(11, 16)}`;
}

function clockRange(start: string, end: string): string {
  return `${caseClockStamp(start)} → ${caseClockStamp(end)}`;
}

/** Group each adjacent tool call/result with the preceding agent summary. */
export function investigationRows(trace: AgentTrace, showMoney = true): AgentInvestigationRow[] {
  const rows: AgentInvestigationRow[] = [];
  let reasoning = '';
  let pendingCall: AgentTraceSchema.AgentTraceStep | null = null;
  // #731: a floor seat reads no US$ in the recorded trace either. The value
  // estimate step states the same measured gap in points instead of the
  // fixture's `US$184/h → US$4,421/day` clause.
  const floorEstimate = !showMoney && trace.steps.at(-1)?.kind === 'recommendation'
    ? estimateBasisLine(recommendationFromTrace(trace).estimated_value)
    : null;

  for (const step of trace.steps) {
    if (step.kind === 'reasoning') {
      reasoning = step.summary ?? '';
      continue;
    }
    if (step.kind === 'tool_call') {
      pendingCall = step;
      continue;
    }
    if (step.kind !== 'tool_result') continue;
    if (!pendingCall || pendingCall.tool !== step.tool || !pendingCall.tool) continue;
    const args = pendingCall.arguments ?? {};
    rows.push({
      tool: pendingCall.tool,
      arguments: args,
      argumentLabel: argumentLabel(pendingCall.tool, args),
      reasoning: publicAgentText(reasoning),
      resultExcerpt: floorEstimate && pendingCall.tool === 'estimate_value'
        ? floorEstimate
        : publicAgentText(step.excerpt ?? ''),
      elapsedMs: step.elapsed_ms,
    });
    pendingCall = null;
    reasoning = '';
  }
  return rows;
}

/** The final step is the recommendation; the trace schema validates that order. */
export function recommendationFromTrace(trace: AgentTrace): AgentRecommendation {
  const step = trace.steps.at(-1);
  if (step?.kind !== 'recommendation' || !step.recommendation) {
    throw new Error('AgentTrace has no final recommendation.');
  }
  return step.recommendation;
}

/** Construct the page-state decision only when a reason accompanies it. */
export function createApprovalRecord(input: {
  caseId: string;
  causeId: string;
  kind: AgentDecisionKind;
  reason: string;
  approver: string;
  at: string;
}): AgentApprovalRecord | null {
  const reason = input.reason.trim();
  if (reason.length < 3) return null;
  return { ...input, reason };
}

/** Forward only an approved FMEA decision to the later living-FMEA caller. */
export function notifyApproval(
  kind: AgentDecisionKind,
  onApproved: ((caseId: string, causeId: string, earlyWarning?: string) => void) | undefined,
  caseId: string,
  causeId: string,
  earlyWarning?: string,
): void {
  if (kind === 'approved') onApproved?.(caseId, causeId, earlyWarning);
}

export function agentToolLabel(tool: AgentTool): string {
  const labels: Record<AgentTool, string> = {
    case_summary: 'case summary',
    get_trend: 'trend',
    get_control_plan_row: 'Control Plan row',
    get_fmea_candidates: 'FMEA candidates',
    get_tarp_ladder: 'TARP ladder',
    get_shift_log: 'shift log',
    estimate_value: 'value estimate',
  };
  return labels[tool];
}

export function agentModelDisplayName(model: string): string {
  if (/^claude-opus-5-5(?:-|$)/i.test(model)) return 'Claude Opus 5.5';
  return model;
}

export function formatAgentDuration(durationMs: number): string {
  const seconds = durationMs / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${Math.round(seconds - minutes * 60)}s`;
}

/** The recorded run's clock only; its future recording date stays off the glass. */
export function agentRecordedTime(recordedAt: string): string {
  return recordedAt.slice(11, 16);
}

/** Read an optional warning only when the recorded recommendation names one. */
export function recommendationEarlyWarning(
  recommendation: AgentRecommendation,
): string | undefined {
  const value = (recommendation as AgentRecommendation & { early_warning?: unknown }).early_warning;
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

export function oneDecimalPercent(fraction: number): string {
  return (Math.round(fraction * 1000) / 10).toFixed(1);
}

export type InvestigationDisplayState = 'waiting' | 'replaying' | 'complete';

export function investigationDisplayState(input: {
  diagnosedAt?: SeriesTime;
  cursor: SeriesTime;
  visibleCount: number;
  totalRows: number;
  playing: boolean;
}): InvestigationDisplayState {
  if (!input.diagnosedAt || input.cursor < input.diagnosedAt) return 'waiting';
  if (input.playing || input.visibleCount < input.totalRows) return 'replaying';
  return 'complete';
}

export function formatAgentEstimate(value: AgentRecommendation['estimated_value']): {
  recovery: string;
  shortfall: string;
  impact: string;
} {
  const comparison = value.recovery_comparison;
  // One basis (D14 / K7): the estimate is the same US$ the case header and the
  // drawer read, so it goes through the shared formatter — never the ISO code
  // and never a bare dollar sign. A non-USD envelope throws instead of dressing
  // a second deck as US$.
  assertUsd(value.currency);
  return {
    // D13: this figure is the rougher proxy, not the plant's one current
    // recovery, so the band names the stage the same way the header does.
    recovery: `rougher recovery ${oneDecimalPercent(comparison.observed)}% vs ${oneDecimalPercent(comparison.expected)}% expected`,
    shortfall: `${oneDecimalPercent(comparison.shortfall)} pt short`,
    impact: `${formatCurrency(value.per_hour)}/h → ${formatCurrency(value.per_day)}/day`,
  };
}

export function agentDecisionLabel(kind: AgentDecisionKind): string {
  return kind === 'approved' ? 'Approved' : kind === 'modified' ? 'Modified' : 'Rejected';
}

/**
 * The floor seats' (Operator, Control room) one-line estimate (#731): the
 * measured rougher recovery gap in per cent and points — the same non-money
 * basis the driver tree's floor projection states — never a US$ figure. It is
 * the money seat's `recovery · shortfall` without the priced impact clause.
 */
export function estimateBasisLine(value: AgentRecommendation['estimated_value']): string {
  const { recovery, shortfall } = formatAgentEstimate(value);
  return `${recovery} · ${shortfall}`;
}

/** Match the persona picker label where the stored persona value is abbreviated. */
export function agentApproverLabel(persona: string): string {
  return persona === 'Metallurgist' ? 'Control metallurgist' : persona;
}

// ---------------------------------------------------------------------------
// K13: the case page's action gate (reconciliation A4, comp Case A).
// ---------------------------------------------------------------------------

/** The TARP ladder rows the analytics run carries (`slices.tarp`); only the join fields are read. */
export type ActionGateTarpLadder = {
  ladder_id: string;
  tiers: readonly { tier: number; owner_role: string }[];
};

export type ActionGateRead = {
  /** The recommended action, first sentence only. */
  proposition: string;
  /** `~US$4,421/day run estimate · TARP tier 2 · owner Control metallurgist` */
  figureLine: string;
  /** `recovers in ~2–4 h · closes after 2 consecutive shift assays at target`; absent without a seeded criterion. */
  recoveryLine?: string;
};

/** First sentence of the recorded Control Plan action; a tag such as `AIT-2101.PV` never splits it. */
export function actionProposition(action: string): string {
  const first = action.split(/(?<=\.)\s+(?=[A-Z])/)[0] ?? action;
  return first.trim().replace(/\.$/, '');
}

/**
 * One read for the action gate: the run estimate (D17 — the envelope's US$,
 * named), the recommendation's TARP tier and that tier's owner from the TARP
 * ladder, then the case's time to recover and close criterion from its seed.
 */
export function actionGateRead(input: {
  recommendation: AgentRecommendation;
  tarpLadders?: readonly ActionGateTarpLadder[];
  recovery?: { lagHours: { min: number; max: number }; closeAfterShiftAssays: number };
  roleLabel: (role: string) => string | undefined;
  /** #731: a floor seat reads the tier and owner, never the priced run estimate. */
  showMoney?: boolean;
}): ActionGateRead {
  const { recommendation, tarpLadders, recovery, roleLabel, showMoney = true } = input;
  assertUsd(recommendation.estimated_value.currency);
  const tier = recommendation.tarp_tier;
  const ownerRole = tarpLadders
    ?.find((ladder) => ladder.ladder_id === recommendation.tarp_ladder_id)
    ?.tiers.find((row) => row.tier === tier)?.owner_role;
  const owner = ownerRole ? roleLabel(ownerRole) ?? ownerRole : undefined;
  const figureLine = [
    ...(showMoney ? [`~${formatCurrency(recommendation.estimated_value.per_day)}/day run estimate`] : []),
    `TARP tier ${tier}`,
    ...(owner ? [`owner ${owner}`] : []),
  ].join(' · ');
  const recoveryLine = recovery
    ? `recovers in ~${recovery.lagHours.min}–${recovery.lagHours.max} h · closes after ${recovery.closeAfterShiftAssays} consecutive shift assays at target`
    : undefined;
  return { proposition: actionProposition(recommendation.control_plan_action), figureLine, recoveryLine };
}

/**
 * The seat word the case page's two gates use (comp Case A): `Metallurgist`
 * approves the action and `Senior metallurgist` the FMEA proposal (D19). The
 * approval record itself keeps the persona picker's `Control metallurgist`.
 */
export function gateSeatLabel(approver: string): string {
  return approver === 'Control metallurgist' ? 'Metallurgist' : approver;
}

/** `Approved · 11:30 · Metallurgist`: the gate pill names the time on the case clock and the approving seat. */
export function approvedPillText(at: string, by: string): string {
  return `Approved · ${at.slice(11, 16)} · ${gateSeatLabel(by)}`;
}

/** A TARP owner role (`metallurgist`, `cro`) as the persona picker names it (`Control metallurgist`, `Control room`). */
export function plantRoleLabel(role: string): string | undefined {
  return PLANT_ROLES.find((row) => row.id === role)?.label;
}
